import React, { useState, useRef, useEffect } from 'react'
import { Brain, Send, RotateCcw, X, AlertCircle, Settings, Key, Upload, FileText, Trash2, Paperclip, Image } from 'lucide-react'
import { useDocument } from '../store/DocumentContext'
import { IntelligentAIService } from '../utils/aiService'
import { DocumentProcessor } from '../utils/documentProcessor'
import type { UploadedDocument } from '../store/DocumentContext'

interface Message {
  id: string
  type: 'user' | 'assistant'
  content: string
  timestamp: Date
}

const AIAssistantPanel: React.FC = () => {
  const { state, dispatch } = useDocument()
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'assistant',
      content: 'Hello! I\'m your intelligent AI legal assistant. I can help you create legal documents, conduct research, review documents, and more. You can also upload reference documents (leases, contracts, prior communications) to provide context for better assistance. Just describe what you need in your own words, and I\'ll guide you through the process with intelligent questions tailored to your specific situation.',
      timestamp: new Date()
    }
  ])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showApiKeyModal, setShowApiKeyModal] = useState(false)
  const [apiKey, setApiKey] = useState('')
  const [provider, setProvider] = useState<'openai' | 'anthropic' | 'gemini'>('gemini')
  const [showUploadedDocs, setShowUploadedDocs] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const aiService = IntelligentAIService.getInstance()

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Check if AI service is configured on mount
  useEffect(() => {
    // Try to load API key from environment variables
    const openaiKey = import.meta.env.VITE_OPENAI_API_KEY || process.env.OPENAI_API_KEY
    const grokKey = import.meta.env.VITE_GROK_API_KEY || process.env.GROK_API_KEY
    const geminiKey = import.meta.env.VITE_GEMINI_API_KEY || process.env.GEMINI_API_KEY || 'AIzaSyBLnkb0B8eiyjB1FuqgljNDSHFjmlFA37Y'
    
    if (geminiKey) {
      aiService.setApiKey(geminiKey, 'gemini')
    } else if (openaiKey) {
      aiService.setApiKey(openaiKey, 'openai')
    } else if (grokKey) {
      // For Grok, we'll use OpenAI client with custom base URL
      aiService.setApiKey(grokKey, 'openai')
    }

    // Apply env-based overrides for model, temperature, and token limits
    const envModel = import.meta.env.VITE_AI_MODEL || import.meta.env.VITE_GROK_MODEL || process.env.GROK_MODEL
    const envTemperatureRaw = import.meta.env.VITE_AI_TEMPERATURE as unknown as string
    const envMaxConvoRaw = import.meta.env.VITE_AI_MAX_TOKENS_CONVO as unknown as string
    const envMaxDocRaw = import.meta.env.VITE_AI_MAX_TOKENS_DOC as unknown as string

    const envTemperature = envTemperatureRaw ? parseFloat(envTemperatureRaw) : undefined
    const envMaxConvo = envMaxConvoRaw ? parseInt(envMaxConvoRaw) : undefined
    const envMaxDoc = envMaxDocRaw ? parseInt(envMaxDocRaw) : undefined

    aiService.setOptions({
      model: typeof envModel === 'string' ? envModel : undefined,
      temperature: typeof envTemperature === 'number' && !Number.isNaN(envTemperature) ? envTemperature : undefined,
      maxTokensConversation: typeof envMaxConvo === 'number' && !Number.isNaN(envMaxConvo) ? envMaxConvo : undefined,
      maxTokensDocument: typeof envMaxDoc === 'number' && !Number.isNaN(envMaxDoc) ? envMaxDoc : undefined
    })
    
    // Only show modal if still not configured
    if (!aiService.isConfigured()) {
      setShowApiKeyModal(true)
    }
  }, [])

  // Handle file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files) return

    for (const file of Array.from(files)) {
      try {
        const processedDoc = await DocumentProcessor.processFile(file)
        const uploadedDoc: UploadedDocument = {
          id: Date.now().toString() + Math.random().toString(36),
          name: file.name,
          content: processedDoc.content,
          type: file.type || 'text/plain',
          size: file.size,
          uploadedAt: new Date(),
          isImage: processedDoc.metadata?.isImage,
          imageUrl: processedDoc.metadata?.imageUrl
        }

        dispatch({ type: 'ADD_UPLOADED_DOCUMENT', payload: uploadedDoc })

        // Show success message with file details
        const wordCount = processedDoc.metadata?.wordCount || 0
        const pageCount = processedDoc.metadata?.pageCount
        const isImage = processedDoc.metadata?.isImage
        const fileSize = DocumentProcessor.formatFileSize(file.size)
        const pageInfo = pageCount ? `, ${pageCount} page${pageCount !== 1 ? 's' : ''}` : ''
        const imageInfo = isImage ? ' (text extracted from image)' : ''
        const successMessage: Message = {
          id: Date.now().toString(),
          type: 'assistant',
          content: `Successfully uploaded "${file.name}" (${fileSize}${pageInfo}, ~${wordCount} words${imageInfo}). I now have this document as context and will reference it when helping you create new documents or answer questions.`,
          timestamp: new Date()
        }
        setMessages(prev => [...prev, successMessage])
      } catch (error) {
        console.error('Error uploading file:', error)
        const errorMessage: Message = {
          id: Date.now().toString(),
          type: 'assistant',
          content: `Failed to upload "${file.name}". ${error instanceof Error ? error.message : 'Please make sure it\'s a supported format (Images, PDF, DOCX, TXT, MD, RTF).'}`,
          timestamp: new Date()
        }
        setMessages(prev => [...prev, errorMessage])
      }
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Remove uploaded document
  const handleRemoveDocument = (docId: string) => {
    const doc = state.uploadedDocuments.find(d => d.id === docId)
    
    // Clean up image URL if it's an image
    if (doc?.isImage && doc.imageUrl) {
      URL.revokeObjectURL(doc.imageUrl)
    }
    
    dispatch({ type: 'REMOVE_UPLOADED_DOCUMENT', payload: docId })
    
    if (doc) {
      const removeMessage: Message = {
        id: Date.now().toString(),
        type: 'assistant',
        content: `Removed "${doc.name}" from context documents.`,
        timestamp: new Date()
      }
      setMessages(prev => [...prev, removeMessage])
    }
  }

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    
    const files = Array.from(e.dataTransfer.files)
    if (files.length === 0) return

    // Create a fake event object for handleFileUpload
    const fakeEvent = {
      target: {
        files: e.dataTransfer.files
      }
    } as React.ChangeEvent<HTMLInputElement>

    await handleFileUpload(fakeEvent)
  }

  // Handle sending messages with intelligent conversation flow
  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue,
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    const currentInput = inputValue
    setInputValue('')
    setIsLoading(true)

    try {
      // Process user input through intelligent AI service with uploaded documents
      const aiResponse = await aiService.processUserInput(currentInput, state.uploadedDocuments)
      
      // Create assistant message
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: aiResponse.content,
        timestamp: new Date()
      }

      setMessages(prev => [...prev, assistantMessage])

      // If document content was generated, update the editor
      if (aiResponse.documentContent) {
        dispatch({ type: 'SET_CONTENT', payload: aiResponse.documentContent })
        
        // Use document type from metadata if available, otherwise default to 'Legal Document'
        const documentTitle = aiResponse.metadata?.documentType || 'Legal Document'
        dispatch({ type: 'SET_TITLE', payload: documentTitle })

        // Show success message
        setTimeout(() => {
          const successMessage: Message = {
            id: (Date.now() + 2).toString(),
            type: 'assistant',
            content: `Your ${documentTitle} has been generated in the main editor. You can now review and modify it as needed. Feel free to ask me to explain any sections, add provisions, or make changes!`,
            timestamp: new Date()
          }
          setMessages(prev => [...prev, successMessage])
        }, 1000)
      }

    } catch (error) {
      console.error('Error processing message:', error)
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: 'I apologize, but I encountered an error while processing your request. Please try again, and if the problem persists, you may need to check your AI service configuration.',
        timestamp: new Date()
      }
      setMessages(prev => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }

  // Handle resetting the conversation
  const handleResetConversation = () => {
    aiService.resetConversation()
    setMessages([
      {
        id: '1',
        type: 'assistant',
        content: 'Hello! I\'m your intelligent AI legal assistant. I can help you create legal documents, conduct research, review documents, and more. You can also upload reference documents (leases, contracts, prior communications) to provide context for better assistance. Just describe what you need in your own words, and I\'ll guide you through the process with intelligent questions tailored to your specific situation.',
        timestamp: new Date()
      }
    ])
  }

  // Handle API key configuration
  const handleConfigureAI = () => {
    setShowApiKeyModal(true)
  }

  // Handle saving API key
  const handleSaveApiKey = () => {
    if (apiKey.trim()) {
      aiService.setApiKey(apiKey.trim(), provider)
      setShowApiKeyModal(false)
      setApiKey('')
      
      // Show configuration success message
      const serviceName = import.meta.env.VITE_GROK_BASE_URL || process.env.GROK_BASE_URL ? 'Grok' : provider.toUpperCase()
      const configMessage: Message = {
        id: Date.now().toString(),
        type: 'assistant',
        content: `AI service configured successfully with ${serviceName}. You can now start creating documents with intelligent AI assistance!`,
        timestamp: new Date()
      }
      setMessages(prev => [...prev, configMessage])
    }
  }

  // Handle key press for sending messages
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  return (
    <div 
      className={`relative flex flex-col h-full bg-white border-l border-gray-200 w-96 ${isDragOver ? 'bg-green-50' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
        <div className="flex items-center space-x-2">
          <Brain className="w-5 h-5 text-green-600" />
          <h3 className="font-semibold text-gray-900">AI Legal Assistant</h3>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowUploadedDocs(!showUploadedDocs)}
            className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors relative"
            title="View Uploaded Documents"
          >
            <FileText className="w-4 h-4" />
            {state.uploadedDocuments.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                {state.uploadedDocuments.length}
              </span>
            )}
          </button>
          <button
            onClick={handleConfigureAI}
            className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors"
            title="Configure AI Service"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={handleResetConversation}
            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
            title="Reset Conversation"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Uploaded Documents Panel */}
      {showUploadedDocs && (
        <div className="border-b border-gray-200 bg-gray-50 p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700">Reference Documents</h4>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="text-xs bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600 transition-colors"
            >
              Upload
            </button>
          </div>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {state.uploadedDocuments.length === 0 ? (
              <p className="text-xs text-gray-500 italic">No documents uploaded yet</p>
            ) : (
                             state.uploadedDocuments.map((doc) => (
                <div key={doc.id} className="bg-white p-2 rounded border">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-2 flex-1 min-w-0">
                      {doc.isImage ? (
                        <Image className="w-3 h-3 text-green-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <FileText className="w-3 h-3 text-gray-500 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-700 truncate" title={doc.name}>
                          {doc.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {DocumentProcessor.formatFileSize(doc.size)} • {doc.uploadedAt.toLocaleDateString()}
                        </div>
                        {doc.isImage && doc.imageUrl && (
                          <div className="mt-1">
                            <img 
                              src={doc.imageUrl} 
                              alt={doc.name}
                              className="max-w-full h-16 object-cover rounded border"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveDocument(doc.id)}
                      className="text-red-500 hover:text-red-700 ml-2"
                      title="Remove document"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg px-4 py-3 ${
                message.type === 'user'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-900'
              }`}
            >
              <div className="whitespace-pre-wrap">{message.content}</div>
            </div>
          </div>
        ))}
        
        {/* Loading indicator */}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-lg px-4 py-3">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                <span className="text-gray-600">AI is thinking...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Drag and Drop Overlay */}
      {isDragOver && (
        <div className="absolute inset-0 bg-green-100 bg-opacity-90 flex items-center justify-center z-40 border-2 border-dashed border-green-400">
          <div className="text-center">
            <Upload className="w-12 h-12 text-green-600 mx-auto mb-2" />
            <p className="text-green-800 font-medium">Drop documents here</p>
            <p className="text-green-600 text-sm">Supported: Images, PDF, DOCX, TXT, MD, RTF</p>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="p-4 border-t border-gray-200">
        {/* File Upload and Document Status */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center space-x-1 text-xs text-gray-600 hover:text-green-600 transition-colors"
              title="Upload reference documents"
            >
              <Paperclip className="w-3 h-3" />
              <span>Upload docs</span>
            </button>
            {state.uploadedDocuments.length > 0 && (
              <span className="text-xs text-green-600">
                {state.uploadedDocuments.length} doc{state.uploadedDocuments.length !== 1 ? 's' : ''} uploaded
              </span>
            )}
          </div>
          <button
            onClick={() => setShowUploadedDocs(!showUploadedDocs)}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            {showUploadedDocs ? 'Hide' : 'Show'} docs
          </button>
        </div>

        <div className="flex space-x-2">
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Describe what you need help with... (e.g., 'I need an employment contract for a software developer')"
            className="flex-1 resize-none border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            rows={2}
            disabled={isLoading}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".txt,.doc,.docx,.pdf,.md,.rtf,.jpg,.jpeg,.png,.gif,.bmp,.webp,text/*,image/*"
          onChange={handleFileUpload}
          className="hidden"
        />
        
        {/* AI Configuration Status */}
        {!aiService.isConfigured() && (
          <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex items-center space-x-2 text-yellow-800">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">AI service not configured. Click the settings icon to configure.</span>
            </div>
          </div>
        )}
      </div>

      {/* API Key Configuration Modal */}
      {showApiKeyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-[90vw]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Configure AI Service</h3>
              <button
                onClick={() => setShowApiKeyModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  AI Provider
                </label>
                <select
                  value={provider}
                  onChange={(e) => setProvider(e.target.value as 'openai' | 'anthropic' | 'gemini')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="gemini">Google Gemini 2.0 Flash</option>
                  <option value="openai">OpenAI (GPT-4)</option>
                  <option value="anthropic">Anthropic (Claude)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Key
                </label>
                <div className="relative">
                  <input
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder={`Enter your ${provider.toUpperCase()} API key`}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <Key className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Your API key is stored locally and never sent to our servers.
                </p>
              </div>
              
              <div className="flex space-x-2 pt-4">
                <button
                  onClick={handleSaveApiKey}
                  disabled={!apiKey.trim()}
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save Configuration
                </button>
                <button
                  onClick={() => setShowApiKeyModal(false)}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AIAssistantPanel
